import type { ReactNode } from 'react';
import { useMemo } from 'react';

import {
    CrossIcon,
    CheckDoubleIcon,
    FileIcon,
    WaitingIcon,
} from '@bestseller-bit/frontend-community.theme.buying-theme';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

const iconSize = 16;

type StateType = 'draft' | 'invalid' | 'missing bom' | 'processing' | 'valid';

interface SingleStateProps {
    type: StateType;
    count?: number;
}

const SingleState = ({ type, count = 0 }: SingleStateProps): ReactNode => {
    const { palette } = useTheme();

    const color = useMemo(() => {
        // Determine color based on state type
        if (type === 'invalid') return palette.error.main;
        if (type === 'valid') return palette.success.main;
        // Every other state uses a semi-transparent primary color
        return alpha(palette.primary.main, 0.5);
    }, [palette.error.main, palette.primary.main, palette.success.main, type]);

    // Determine icon based on state type
    const icon = useMemo(() => {
        switch (type) {
            case 'draft':
                return (
                    <FileIcon
                        size={iconSize}
                        color={color}
                    />
                );
            case 'invalid':
                return (
                    <CrossIcon
                        size={iconSize}
                        color={color}
                    />
                );
            case 'missing bom':
                return null;
            case 'processing':
                return (
                    <WaitingIcon
                        size={iconSize}
                        color={color}
                    />
                );
            case 'valid':
                return (
                    <CheckDoubleIcon
                        size={iconSize}
                        color={color}
                    />
                );
            default:
                return undefined;
        }
    }, [type, color]);

    // Render them together
    return (
        <Stack
            direction="row"
            spacing={1}
            sx={{
                alignItems: 'center',
            }}
        >
            {icon}
            <Typography
                variant="buttons.xsmall"
                sx={{ color }}
            >
                {count > 0 ? `${count} ` : ''}
                {type.toUpperCase()}
            </Typography>
        </Stack>
    );
};

export { SingleState };
